import React from 'react';
import { Card } from '../../ui/Card';
import { motion } from 'framer-motion';
import { PieChart as PieIcon } from 'lucide-react';

interface DataPoint {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  title: string;
  data: DataPoint[];
}

export const PieChart: React.FC<PieChartProps> = ({ title, data }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  let currentAngle = 0;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-100 rounded-lg">
          <PieIcon className="w-5 h-5 text-purple-600" />
        </div>
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>

      <div className="flex items-center justify-between">
        <div className="relative w-48 h-48">
          <svg viewBox="0 0 100 100" className="transform -rotate-90">
            {data.map((item, i) => {
              const percentage = (item.value / total) * 100;
              const angle = (percentage / 100) * 360;
              const slice = (
                <motion.path
                  key={i}
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: i * 0.2 }}
                  d={`
                    M 50 50
                    L ${50 + 40 * Math.cos((currentAngle * Math.PI) / 180)} ${
                    50 + 40 * Math.sin((currentAngle * Math.PI) / 180)
                  }
                    A 40 40 0 ${angle > 180 ? 1 : 0} 1 ${
                    50 + 40 * Math.cos(((currentAngle + angle) * Math.PI) / 180)
                  } ${50 + 40 * Math.sin(((currentAngle + angle) * Math.PI) / 180)}
                    Z
                  `}
                  fill={item.color}
                />
              );
              currentAngle += angle;
              return slice;
            })}
          </svg>
        </div>

        <div className="space-y-4">
          {data.map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${item.color}`} />
              <div>
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
                <span className="text-sm text-gray-500 ml-2">
                  {Math.round((item.value / total) * 100)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};